import { FormFields, FormFieldSchema, FormSections } from '../interfaces/job-information-form.interface';

const companyName: FormFieldSchema = {
  name: 'company_name',
  label: $localize`:@@featureUserProfile-companyName:Unternehmensname`,
  placeholder: $localize`:@@featureUserProfile-companyName-placeholder:Unternehmensname`,
  validations: { required: true },
};
const website: FormFieldSchema = {
  name: 'website',
  label: $localize`:@@featureUserProfile-website:Unternehmens-URL`,
  placeholder: 'https://www....',
  type: 'url',
};
const employeeCount: FormFieldSchema = {
  name: 'employee_count',
  label: $localize`:@@featureUserProfile-employeeCount:Unternehmensgröße`,
  placeholder: $localize`:@@featureUserProfile-employeeCount-placeholder:Bitte wählen`,
  type: 'select',
};
const areaOfInterest: FormFieldSchema = {
  name: 'area_of_interest',
  label: $localize`:@@featureUserProfile-areaOfInterest:Interessenschwerpunkt`,
  placeholder: $localize`:@@featureUserProfile-areaOfInterest-placeholder:Bitte wählen`,
  type: 'select',
};
export const title: FormFieldSchema = {
  name: 'title',
  label: $localize`:@@featureUserProfile-title:Titel`,
  placeholder: $localize`:@@featureUserProfile-title-placeholder:Bitte wählen`,
  type: 'select',
};
const position: FormFieldSchema = {
  name: 'position',
  label: $localize`:@@featureUserProfile-position:Position`,
  placeholder: $localize`:@@featureUserProfile-position-placeholder:Position`,
};
const streetAddress: FormFieldSchema = {
  name: 'street_address',
  label: $localize`:@@featureUserProfile-street_address:Straße & Hausnr.`,
  placeholder: $localize`:@@featureUserProfile-street_address-placeholder:Straße & Hausnr.`,
  validations: { required: true },
};
const postalCode: FormFieldSchema = {
  name: 'postal_code',
  label: $localize`:@@featureUserProfile-postal_code:PLZ`,
  placeholder: $localize`:@@featureUserProfile-postal_code-placeholder:PLZ`,
  spans: { tablet: 2, desktop: 4 },
  validations: { required: true },
};
const locality: FormFieldSchema = {
  name: 'locality',
  label: $localize`:@@featureUserProfile-locality:Ort`,
  placeholder: $localize`:@@featureUserProfile-locality-placeholder:Ort`,
  spans: { tablet: 6, desktop: 8 },
  validations: { required: true },
};
export const countryName: FormFieldSchema = {
  name: 'country_name',
  label: $localize`:@@featureUserProfile-country_name:Land`,
  placeholder: $localize`:@@featureUserProfile-country_name-placeholder:Bitte wählen`,
  validations: { required: true },
  type: 'select',
};
const region: FormFieldSchema = {
  name: 'region',
  label: $localize`:@@featureUserProfile-region:Bundesland`,
  placeholder: $localize`:@@featureUserProfile-region-placeholder:Bundesland`,
};
const phoneNumber: FormFieldSchema = {
  name: 'phone_number',
  label: $localize`:@@featureUserProfile-phone_number:Telefonnr. geschäftlich`,
  placeholder: $localize`:@@featureUserProfile-phone_number-placeholder:Telefonnr. geschäftlich`,
  type: 'tel',
};
const mobileNumber: FormFieldSchema = {
  name: 'mobile_number',
  label: $localize`:@@featureUserProfile-mobile_number:Telefonnr. mobil`,
  placeholder: $localize`:@@featureUserProfile-mobile_number-placeholder:Telefonnr. mobil`,
  type: 'tel',
};
const additionalEmail: FormFieldSchema = {
  name: 'additional_email',
  label: $localize`:@@featureUserProfile-additional_email:Alternative E-Mail Adresse`,
  placeholder: $localize`:@@featureUserProfile-additional_email-placeholder:Alternative E-Mail Adresse`,
  type: 'email',
};
const linkedIn: FormFieldSchema = {
  name: 'linkedin',
  label: $localize`:@@featureUserProfile-linkedin:LinkedIn Profil`,
  placeholder: 'https://www....',
  type: 'url',
};
const xing: FormFieldSchema = {
  name: 'xing',
  label: $localize`:@@featureUserProfile-xing:Xing Profil`,
  placeholder: 'https://www....',
  type: 'url',
};
const facebook: FormFieldSchema = {
  name: 'facebook',
  label: $localize`:@@featureUserProfile-facebook:Facebook Profil`,
  placeholder: 'https://www....',
  type: 'url',
};
const businessCardMessage: FormFieldSchema = {
  name: 'business_card_message',
  label: $localize`:@@featureUserProfile-business_card_message:Weitere Informationen`,
  placeholder: $localize`:@@featureUserProfile-business_card_message-placeholder:z. B. Informationen zu Ihrer Biographie`,
  type: 'textarea',
  validations: { limit: 200 },
};

/**
 * @description
 * Contains the plain field definitions to build up the `userJobInformationFormGroup`.
 * Beware of a mismatch between the fields defined here and the fields defined in `formSections`
 */
export const formFields: FormFields = [
  companyName,
  website,
  employeeCount,
  areaOfInterest,
  title,
  position,
  streetAddress,
  postalCode,
  locality,
  countryName,
  region,
  phoneNumber,
  mobileNumber,
  additionalEmail,
  linkedIn,
  xing,
  facebook,
  businessCardMessage,
];

/**
 * @description
 * Returns a flat list of the field names and labels of the fields expected to be present in a business card.
 */
export const jobInformationFields: { name: string; label: string }[] = formFields.map(({ name, label }) => ({
  name,
  label,
}));

/**
 * @description
 * Returns a flat list of the field names of the required fields to
 * create a business card.
 */
export const requiredFields: { name: string; label: string }[] = formFields
  .filter((field) => field.validations?.required)
  .map(({ name, label }) => ({ name, label }));

/**
 * @description
 * Contains the information wich fields are visually aligned together.
 * Beware of a mismatch between the fields defined in the sections and the fields defined in `formFields`
 */
export const formSections: FormSections = [
  {
    title: $localize`:@@featureUserProfile-companyAddressSection-title:Geschäftsadresse`,
    fields: [companyName, countryName, streetAddress, postalCode, locality, region, website],
  },
  {
    title: $localize`:@@featureUserProfile-professionSection-title:Berufsbezeichnung`,
    fields: [title, position],
  },
  {
    title: $localize`:@@featureUserProfile-contactSection-title:Kontaktdaten`,
    fields: [phoneNumber, mobileNumber, additionalEmail],
  },
  {
    title: $localize`:@@featureUserProfile-socialMediaSection-title:Social Media`,
    fields: [linkedIn, xing, facebook],
  },
  {
    title: $localize`:@@featureUserProfile-additionalInfoSection-title:Weitere Angaben`,
    fields: [areaOfInterest, employeeCount, businessCardMessage],
  },
];
